import { SupplierInventoryDeltaResponseDto } from "../types";
import { applyAdjustment, SupplierPart } from "./partsCalculationUtils";

/**
 * Build the composite key used to look up a delta quantity
 * for a specific supplier + root part family.
 */
export const getSupplierInventoryDeltaKey = (
  supplierId: string,
  rootPartNumber: string,
): string => `${supplierId}::${rootPartNumber}`;

/**
 * Apply consumption deltas to supplier parts.
 *
 * Quantity adjustment after offline merge and before part confirmation:
 *   Typesense parts → offline merge → THIS → part confirmation → metrics
 *
 * For each part, the adjusted quantity is:
 *   Math.max(0, familyQuantity - deltaQuantity)
 *
 * Parts without a matching delta entry are returned unchanged.
 */
export const applySupplierInventoryDeltasToParts = <T extends SupplierPart>({
  supplierId,
  parts,
  supplierInventoryDeltaQuantityMap,
}: {
  supplierId: string;
  parts: T[];
  supplierInventoryDeltaQuantityMap?: Map<string, number>;
}): T[] => {
  if (!supplierInventoryDeltaQuantityMap?.size || !supplierId) return parts;

  return parts.map((part) => {
    const rootPartNumber = part.rootPartNumber;
    if (!rootPartNumber) return part;

    const supplierInventoryDeltaQuantity =
      supplierInventoryDeltaQuantityMap.get(
        getSupplierInventoryDeltaKey(supplierId, rootPartNumber),
      ) ?? 0;

    if (supplierInventoryDeltaQuantity <= 0) return part;

    const familyQuantity = Number(part.familyQuantity) || 0;
    const adjustedFamilyQuantity = Math.max(
      0,
      familyQuantity - supplierInventoryDeltaQuantity,
    );

    return applyAdjustment(
      part,
      {
        label: "Consumption",
        value: supplierInventoryDeltaQuantity,
      },
      adjustedFamilyQuantity,
    );
  });
};

/**
 * Build a lookup map from the delta API response.
 *
 * Key:   "supplierId::rootPartNumber"
 * Value: deltaQuantity only (self consumption).
 *        Confirmed / pending part-confirmation qty is applied by
 *        `buildSupplierPartConfirmationQuantityMap` so it is not subtracted twice.
 *
 * Returns an empty map if response is undefined or has no results.
 */
export const buildSupplierInventoryDeltaQuantityMap = (
  response: SupplierInventoryDeltaResponseDto | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  if (!response?.results) return map;

  for (const delta of response.results) {
    if (!delta.supplierId || !delta.rootPartNumber) continue;
    const consumptionQuantity = Number(delta.deltaQuantity) || 0;
    if (consumptionQuantity <= 0) continue;
    map.set(
      getSupplierInventoryDeltaKey(delta.supplierId, delta.rootPartNumber),
      consumptionQuantity,
    );
  }
  return map;
};
